
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/subscription';

import { useAdminStatus } from '@/pages/feedback-admin/hooks/useAdminStatus';
import { cn } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import {
  Book, Bookmark, Box, Calendar, MoreHorizontal, UserCircle,
  Sparkles, Share2, Settings, Crown
} from 'lucide-react';
import { OfflineAlertSyncButton } from '@/components/offline/ManualSyncControls';

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSubscribed } = useSubscription();
  // const { user } = usePermissions();
  const { isAdmin } = useAdminStatus();

  const navItems = [
    {
      name: 'Journal',
      icon: Book,
      path: '/journal',
      active: location.pathname === '/journal',
    },
    {
      name: 'Techniques',
      icon: Bookmark,
      path: '/techniques',
      active: location.pathname === '/techniques' || location.pathname.startsWith('/techniques/'),
    },
    {
      name: 'My Techniques',
      icon: Box,
      path: '/my-techniques',
      premium: true,
      active: location.pathname === '/my-techniques',
    },
    {
      name: 'Game Planner',
      icon: Calendar,
      path: '/game-planner2',
      premium: true,
      active: location.pathname === '/game-planner2' || location.pathname === '/game-planner',
    },
    {
      name: 'More',
      icon: MoreHorizontal,
      path: '#',
      action: true,
      active: false,
    },
  ];

  const handleMoreMenuItemClick = (path: string) => {
    if (path === '/affiliates') {
      navigate('/affiliates');
    } else {
      navigate(path);
    }
  };

  return (
  <div
    className="fixed left-0 right-0 bottom-0 z-50 flex items-center justify-around bg-background border-t border-border"
    style={{
      paddingBottom: 'env(safe-area-inset-bottom)',
      height: 'calc(4rem + env(safe-area-inset-bottom))',
    }}
  >
      {navItems.map((item) => {
        if (item.action) {
          return (
            <DropdownMenu key={item.name}>
              <DropdownMenuTrigger className="flex h-full w-full flex-col items-center justify-center py-3 text-muted-foreground hover:text-foreground">
                <item.icon className="h-6 w-6 mb-1.5" />
                <span className="text-sm font-medium">{item.name}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* Sync Button */}
                <div className="p-2">
                  <OfflineAlertSyncButton size="sm" className="w-full" />
                </div>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => handleMoreMenuItemClick('/affiliates')} className="py-2">
                  <Share2 className="mr-2 h-5 w-5" />
                  <span>Referrals</span>
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => handleMoreMenuItemClick('/profile')} className="py-2">
                  <UserCircle className="mr-2 h-5 w-5" />
                  <span>Profile</span>
                </DropdownMenuItem>

                {!isSubscribed && (
                  <DropdownMenuItem onClick={() => handleMoreMenuItemClick('/subscription')} className="py-2">
                    <Sparkles className="mr-2 h-5 w-5" />
                    <span>Subscription</span>
                  </DropdownMenuItem>
                )}

                {isAdmin && (
                  <DropdownMenuItem onClick={() => handleMoreMenuItemClick('/roadmap-admin')} className="py-2">
                    <Settings className="mr-2 h-5 w-5" />
                    <span>Admin</span>
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />

                {/* Temporarily hidden - keeping in codebase for future use */}
                {/*
                <DropdownMenuItem onClick={() => handleMoreMenuItemClick('/debug/subscription')} className="py-2">
                  <Bug className="mr-2 h-5 w-5" />
                  <span>Subscription Debug</span>
                </DropdownMenuItem>
                */}

              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        return (
          <Link
            key={item.name}
            to={item.premium && !isSubscribed ? '/subscription' : item.path}
            className={cn(
              'flex h-full w-full flex-col items-center justify-center py-3 relative',
              item.active ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground',
            )}
            aria-label={item.name}
          >
            <div className="relative">
              <item.icon className="h-6 w-6 mb-1.5" />
              {item.premium && !isSubscribed && (
                <Crown className="h-3 w-3 absolute -top-1 -right-1 text-yellow-500" />
              )}
            </div>
            <span className="text-sm font-medium">{item.name}</span>
          </Link>
        );
      })}
    </div>
  );
}
